import React, { useState } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../../components/ui/table';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription, DialogClose
} from '../../../components/ui/dialog';
import { 
  Card, CardContent
} from '../../../components/ui/card';
import { 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  Clock, 
  AlertCircle, 
  Tag,
  Check as CheckIcon,
  X as XIcon
} from 'lucide-react';
import { useToast } from '../../../components/ui/use-toast';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAdminArtCategories, createCategory, updateCategory, deleteCategory } from '../../../services/adminPortfolioService';
import { ArtCategory } from '../../../services/portfolioService';
import { useDomainQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';

export const CategoryList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<ArtCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  
  // État pour le formulaire d'ajout/modification
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ArtCategory | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain } = getFilterParams();
  
  // Récupérer les catégories de projets
  const { data: categories, error, isLoading, refetch } = useQuery({
    queryKey: ['adminArtCategories', domain],
    queryFn: () => fetchAdminArtCategories(domain || '')
  });

  // Filtrer les catégories par recherche
  const filteredCategories = categories?.filter(category => 
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Gestion de la suppression
  const handleDeleteClick = (category: ArtCategory) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    setDeletingCategory(true);
    try {
      await deleteCategory(categoryToDelete.id);
      
      toast({
        title: 'Catégorie supprimée',
        description: 'La catégorie de projet a été supprimée avec succès',
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtCategories'] });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la catégorie de projet',
        variant: 'destructive',
      });
    } finally {
      setDeletingCategory(false);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  // Gestion de l'ajout/modification
  const handleAddClick = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategorySlug('');
    setIsFormDialogOpen(true);
  };

  const handleEditClick = (category: ArtCategory) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategorySlug(category.slug || '');
    setIsFormDialogOpen(true);
  };
  
  // Générer un slug à partir du nom
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };
  
  // Mettre à jour le slug quand le nom change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setCategoryName(name);
    // Générer le slug automatiquement si on est en mode création ou si le slug n'a pas été modifié manuellement
    if (!editingCategory || categorySlug === generateSlug(categoryName)) {
      setCategorySlug(generateSlug(name));
    }
  };
  
  // Gérer la modification manuelle du slug
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategorySlug(e.target.value);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast({
        title: 'Erreur',
        description: 'Le nom de la catégorie de projet est requis',
        variant: 'destructive',
      });
      return;
    }
    
    setSavingCategory(true);
    try {
      // Récupérer le domaine actuel
      const { domain } = getFilterParams();
      
      if (editingCategory) {
        // Mise à jour
        await updateCategory(editingCategory.id, {
          name: categoryName,
          slug: categorySlug,
          updated_at: new Date().toISOString()
        });
        
        toast({
          title: 'Catégorie mise à jour',
          description: 'La catégorie de projet a été mise à jour avec succès',
        });
      } else {
        // Création
        await createCategory({
          name: categoryName,
          slug: categorySlug,
          ...(domain && { domain }) // Inclure domain seulement si défini
        });
        
        toast({
          title: 'Catégorie créée',
          description: 'La catégorie de projet a été créée avec succès',
        });
      }
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtCategories'] });
      
      // Fermer le dialogue
      setIsFormDialogOpen(false);
      setCategoryName('');
      setEditingCategory(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la catégorie',
        variant: 'destructive',
      });
    } finally {
      setSavingCategory(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Catégories du Portfolio</h1>
          <Button 
            onClick={handleAddClick} 
            variant="default" 
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            <Plus className="mr-2 h-4 w-4" /> Nouvelle catégorie
          </Button>
        </div>

        {/* Recherche */}
        <div className="mb-8 bg-black/20 backdrop-blur-sm rounded-xl border border-white/10 p-6 shadow-lg">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-300 mb-1">Rechercher</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Rechercher une catégorie..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                />
              </div>
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => setSearchQuery('')}
              className="border-white/10 text-white hover:bg-white/5"
              disabled={!searchQuery}
            >
              <XIcon className="mr-2 h-4 w-4" /> Réinitialiser
            </Button>
          </div>
        </div>

        {/* Liste des catégories */}
        <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
          <CardContent className="p-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Clock className="animate-spin h-8 w-8 text-yellow-500 mr-3" />
                <span className="text-white text-lg">Chargement...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">Erreur de chargement</h3>
                <p className="text-gray-400">Impossible de récupérer les catégories</p>
                <Button onClick={() => refetch()} className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black">
                  Réessayer
                </Button>
              </div>
            ) : filteredCategories.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/10">
                      <TableHead className="text-left text-sm font-medium text-gray-300">Nom</TableHead>
                      <TableHead className="text-left text-sm font-medium text-gray-300">Nombre d'œuvres</TableHead>
                      <TableHead className="text-right text-sm font-medium text-gray-300">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCategories.map((category, index) => (
                      <TableRow key={category.id} className={`border-b border-white/5 hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                        <TableCell className="py-3 px-4">
                          <div className="flex items-center">
                            <Tag className="h-5 w-5 text-yellow-500 mr-2" />
                            <span className="font-medium text-white">{category.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-gray-300">
                          {category.artwork_count || 0}
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(category)}
                              className="text-yellow-400 hover:text-yellow-300"
                            >
                              <Edit size={18} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(category)}
                              className="text-red-400 hover:text-red-300"
                              disabled={category.artwork_count > 0}
                            >
                              <Trash2 size={18} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <Tag className="h-12 w-12 text-gray-500 mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">Aucune catégorie trouvée</h3>
                <p className="text-gray-400">
                  {searchQuery 
                    ? 'Aucune catégorie ne correspond à votre recherche.' 
                    : 'Commencez par ajouter une nouvelle catégorie.'}
                </p>
                {searchQuery && (
                  <Button 
                    onClick={() => setSearchQuery('')}
                    className="mt-4 border-white/10 text-white hover:bg-white/5"
                    variant="outline"
                  >
                    Réinitialiser la recherche
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialogue de confirmation de suppression */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">Supprimer la catégorie</DialogTitle>
              <DialogDescription className="text-gray-400">
                Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>

            {categoryToDelete && (
              <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center">
                  <Tag className="h-5 w-5 text-yellow-500 mr-2" />
                  <span className="font-medium text-white">{categoryToDelete.name}</span>
                </div>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deletingCategory}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deletingCategory ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialogue d'ajout/modification de catégorie */}
        <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
          <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">
                {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                {editingCategory 
                  ? 'Modifiez les informations de la catégorie' 
                  : 'Créez une nouvelle catégorie pour vos œuvres'}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="category-name" className="block text-sm font-medium text-gray-300 mb-1">
                  Nom de la catégorie *
                </label>
                <div className="space-y-4">
                  <div>
                    <Input
                      id="category-name"
                      value={categoryName}
                      onChange={handleNameChange}
                      placeholder="Nom de la catégorie"
                      className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="category-slug" className="block text-sm font-medium text-gray-300 mb-1">
                      Slug (URL) *
                      <span className="text-xs text-gray-400 ml-1">
                        {categorySlug && `(sera visible comme: /portfolio/categorie/${categorySlug})`}
                      </span>
                    </label>
                    <Input
                      id="category-slug"
                      value={categorySlug}
                      onChange={handleSlugChange}
                      placeholder="slug-de-la-categorie"
                      className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 w-full font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Le slug est utilisé dans l'URL. Utilisez des tirets (-) au lieu d'espaces.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setIsFormDialogOpen(false)}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Annuler
              </Button>
              <Button
                variant="default"
                onClick={handleSaveCategory}
                disabled={savingCategory || !categoryName.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {savingCategory ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckIcon className="mr-2 h-4 w-4" />
                    {editingCategory ? 'Mettre à jour' : 'Créer'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default CategoryList;
